import { useEffect, useRef, useState } from 'react';
import { searchRenters, type RenterSuggestion } from '../api';

type Props = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onSelect: (renter: RenterSuggestion) => void;
  placeholder?: string;
};

const inputCls =
  'w-full border border-ink-200 bg-cream-50 rounded-lg px-2.5 py-1.5 text-sm text-ink-900 placeholder:text-[#a89d8a] focus:outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-500/30 transition-colors';

export default function RenterAutocomplete({
  label, value, onChange, onSelect, placeholder = '—',
}: Props) {
  const [suggestions, setSuggestions] = useState<RenterSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const justSelected = useRef(false);

  useEffect(() => {
    const v = value.trim();
    if (!v) { setSuggestions([]); setOpen(false); return; }
    if (justSelected.current) { justSelected.current = false; return; }
    const tid = setTimeout(async () => {
      const results = await searchRenters(v);
      setSuggestions(results);
      setOpen(results.length > 0);
    }, 250);
    return () => clearTimeout(tid);
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-xs text-ink-500 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={e => e.key === 'Escape' && setOpen(false)}
        className={inputCls}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-30 bg-cream-50 border border-ink-200 rounded-lg shadow-card-md mt-1 max-h-64 overflow-y-auto">
          {suggestions.map(r => (
            <li
              key={r.id}
              onMouseDown={e => {
                e.preventDefault();
                justSelected.current = true;
                onSelect(r);
                setOpen(false);
              }}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-sage-50 flex items-baseline justify-between gap-3"
            >
              <span className="font-medium text-ink-900">{r.name}</span>
              <span className="text-xs text-ink-500 truncate">
                {r.phone || r.email || ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
